import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';

/**
 * ProtectedRoute component to restrict access based on module permissions
 * 
 * Usage:
 * <Route path="/funds" element={<ProtectedRoute module="funds"><Funds /></ProtectedRoute>} />
 */
const ProtectedRoute = ({ children, module, requireAdmin = false, requireOwner = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user is logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if owner role is required
  if (requireOwner && user.user_type !== 'owner') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start max-w-2xl mx-auto mt-12">
          <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="text-red-900 font-semibold text-lg mb-2">Access Denied</h3>
            <p className="text-red-700">
              This page is only accessible to platform owners. You don't have the required permissions.
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if admin role is required
  if (requireAdmin && user.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start max-w-2xl mx-auto mt-12">
          <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="text-red-900 font-semibold text-lg mb-2">Access Denied</h3>
            <p className="text-red-700">
              You don't have permission to access this page. Only administrators can access this feature.
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check module permission if specified
  if (module && user.effective_permissions) {
    const hasPermission = user.effective_permissions[module] === true;
    
    if (!hasPermission) {
      return (
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-start max-w-2xl mx-auto mt-12">
            <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" size={24} />
            <div>
              <h3 className="text-yellow-900 font-semibold text-lg mb-2">Access Denied</h3>
              <p className="text-yellow-700">
                You don't have permission to access the <strong>{module}</strong> module. 
                Please contact your administrator if you need access.
              </p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // User has permission, render the children
  return children;
};

export default ProtectedRoute;
